import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, X, Search, ChevronDown, Filter } from "lucide-react";
import { cn } from "@/lib/utils";
import { filterStyleVars } from "@/lib/filterStyle";
import { smartSortValues } from "@/lib/filterValues";

// Item 165 v2: enterprise Multi Filter — multi-select checkboxes, search, select/clear all,
// apply/cancel mode, chips, cascading, and a Compact Mode that collapses into a "Filters" button.
export function MultiFilterWidget({ widget, publicToken = null, values = {}, onChange, globalFilters = [] }) {
  const fields = (widget.config?.fields || []).filter((f) => f && f.field);
  const opts = widget.config?.options || {};
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 400, h: 400 });
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((es) => {
      const r = es[0]?.contentRect;
      if (r) setSize({ w: r.width, h: r.height });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  // Item 31: in Apply mode every field stages its selection and ONE common Apply runs them together.
  const [staged, setStaged] = useState({});

  // seed per-field defaults once (only when the slicer state has never been touched)
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !onChange) return;
    seeded.current = true;
    fields.forEach((f) => {
      if (values[f.field] === undefined && f.default != null && f.default !== "") {
        const dv = String(f.default).split(",").map((s) => s.trim()).filter(Boolean);
        onChange(f.field, f.multi ? dv : dv[0]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!widget.dataset_id || !fields.length) {
    return (
      <div ref={ref} className="h-full w-full flex items-center justify-center">
        <div data-testid="multifilter-empty" className="text-center text-[color:var(--ink-3)] text-sm px-4">
          <SlidersHorizontal className="w-6 h-6 mx-auto mb-2 opacity-60" />
          <div className="font-medium text-[color:var(--ink-2)]">Add filter fields</div>
          <div className="text-xs mt-1">Pick a dataset and one or more fields in the properties panel.</div>
        </div>
      </div>
    );
  }

  const isActive = (v) => (Array.isArray(v) ? v.length > 0 : v != null && v !== "");
  const applyMode = opts.applyMode === "apply";
  const effValues = applyMode ? { ...values, ...staged } : values;
  const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const pending = Object.keys(staged).filter((f) => !same(staged[f], values[f]));
  const activeCount = fields.filter((f) => isActive(effValues[f.field])).length;
  const select = (field, val) => {
    if (applyMode) setStaged((s) => ({ ...s, [field]: val }));
    else if (onChange) onChange(field, val);
  };
  const applyAll = () => {
    Object.entries(staged).forEach(([f, v]) => onChange && onChange(f, v));
    setStaged({});
  };
  const cancelStaged = () => setStaged({});
  const clearAll = () => {
    if (applyMode) setStaged(Object.fromEntries(fields.map((f) => [f.field, null])));
    else fields.forEach((f) => onChange && onChange(f.field, null));
  };

  const vars = filterStyleVars(opts);
  const mode = opts.compactMode || "auto";
  const threshold = Number(opts.compactThreshold) || 200;
  const compact = mode === "on" || (mode === "auto" && (size.w < threshold || size.h < 130));

  const applyBar = applyMode && (
    <div data-testid="multifilter-apply-bar" className="flex items-center gap-1.5 pt-1.5 border-t border-[color:var(--glass-border)] sticky bottom-0 bg-[color:var(--surface)]">
      <Button data-testid="multifilter-apply-all" size="sm" disabled={!pending.length}
        className="h-6 text-[10px] flex-1 bg-[color:var(--brand-ocean)] text-white disabled:opacity-50"
        onClick={applyAll}>Apply{pending.length ? ` (${pending.length})` : ""}</Button>
      <Button data-testid="multifilter-cancel-all" size="sm" variant="outline" disabled={!pending.length}
        className="h-6 text-[10px] flex-1" onClick={cancelStaged}>Cancel</Button>
    </div>
  );

  if (compact) {
    return (
      <div ref={ref} className="fw-root h-full w-full flex items-center justify-center p-1" style={vars}>
        <CompactFilters widget={widget} fields={fields} values={effValues} onSelect={select}
          publicToken={publicToken} globalFilters={globalFilters} activeCount={activeCount} clearAll={clearAll}
          opts={opts} applyMode={applyMode} applyBar={applyBar} pendingCount={pending.length} />
      </div>
    );
  }

  return (
    <div ref={ref} data-testid="multi-filter-widget" className="fw-root h-full w-full overflow-auto px-1 py-1 space-y-2.5" style={vars}>
      <div className="flex items-center gap-2">
        {activeCount > 0 ? (
          <button data-testid="multifilter-clear-all" onClick={clearAll}
            className="text-[10px] text-[color:var(--brand-ocean)] hover:underline flex items-center gap-0.5"><X className="w-3 h-3" />Clear all ({activeCount})</button>
        ) : (
          <span data-testid="multifilter-clear-all" aria-disabled="true"
            className="text-[10px] text-[color:var(--ink-3)] opacity-60 flex items-center gap-0.5 cursor-default select-none"><X className="w-3 h-3" />Clear all</span>
        )}
        {!!pending.length && <span data-testid="multifilter-pending-badge" className="text-[9px] px-1.5 py-0.5 rounded-full bg-[color:rgba(43,78,255,0.12)] text-[color:var(--brand-ocean)]">{pending.length} not applied</span>}
      </div>
      {fields.map((f) => (
        <FilterField key={f.field} datasetId={widget.dataset_id} f={f}
          publicToken={publicToken} value={effValues[f.field]} onSelect={select} globalFilters={globalFilters}
          opts={opts} applyMode={applyMode} pending={!same(staged[f.field], values[f.field]) && staged[f.field] !== undefined} />
      ))}
      {applyBar}
    </div>
  );
}

function CompactFilters({ widget, fields, values, onSelect, publicToken, globalFilters, activeCount, clearAll, opts, applyMode, applyBar, pendingCount = 0 }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="multifilter-compact-btn"
          className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors",
            activeCount > 0
              ? "bg-[color:rgba(43,78,255,0.10)] border-[color:rgba(43,78,255,0.35)] text-[color:var(--brand-ocean)]"
              : "bg-[color:var(--surface)] border-[color:var(--glass-border)] text-[color:var(--ink-2)] hover:border-[color:var(--brand-ocean)]")}>
          <Filter className="w-3.5 h-3.5" />
          {opts.compactLabel || "Filters"}
          {activeCount > 0 && <span data-testid="multifilter-compact-badge" className="min-w-[16px] h-4 px-1 rounded-full bg-[color:var(--brand-ocean)] text-white text-[9px] flex items-center justify-center">{activeCount}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="multifilter-compact-popup" align="start" className="fw-dropdown w-[300px] max-h-[70vh] overflow-auto p-3 space-y-2.5" style={filterStyleVars(opts)}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-[color:var(--ink)]">{opts.compactLabel || "Filters"}</span>
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <button data-testid="multifilter-popup-clear" onClick={clearAll} className="text-[10px] text-[color:var(--brand-ocean)] hover:underline">Clear all</button>
            )}
            <button onClick={() => setOpen(false)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><X className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        {fields.map((f) => (
          <FilterField key={f.field} datasetId={widget.dataset_id} f={f}
            publicToken={publicToken} value={values[f.field]} onSelect={onSelect} globalFilters={globalFilters} opts={opts}
            applyMode={applyMode}
            onApplied={!applyMode && opts.closeOnApply !== false ? () => setOpen(false) : undefined} />
        ))}
        {applyBar}
      </PopoverContent>
    </Popover>
  );
}

function FilterField({ datasetId, f, publicToken, value, onSelect, globalFilters, opts, onApplied, applyMode = false, pending = false }) {
  const field = f.field;
  const label = f.label || field;
  const multi = !!f.multi;
  const [allValues, setAllValues] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const others = (globalFilters || []).filter((x) => x.field !== field);
  const othersKey = JSON.stringify(others);
  useEffect(() => {
    let active = true;
    const req = publicToken
      ? api.post("/public/distinct", { token: publicToken, dataset_id: datasetId, field, filters: others })
      : api.post(`/datasets/${datasetId}/distinct`, { field, filters: others });
    req.then(({ data }) => { if (active) setAllValues(smartSortValues((data.values || []).map(String), f.sort || opts.sort || "asc", f.sort_kind || opts.sort_kind)); }).catch(() => { if (active) setAllValues([]); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, datasetId, publicToken, othersKey]);

  const selected = useMemo(() => {
    if (Array.isArray(value)) return value.map(String);
    return value != null && value !== "" ? [String(value)] : [];
  }, [value]);

  const setSel = (arr) => {
    const out = multi ? (arr.length ? arr : null) : (arr[0] ?? null);
    onSelect && onSelect(field, out);
  };
  const toggle = (v) => {
    if (multi) setSel(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    else { setSel(selected.includes(v) ? [] : [v]); if (!applyMode) setOpen(false); }
  };
  const shown = q.trim() ? allValues.filter((v) => v.toLowerCase().includes(q.trim().toLowerCase())) : allValues;
  const maxChips = Number(opts.maxChips) || 2;
  const committed = selected;

  return (
    <div data-testid={`multifilter-row-${field}`}>
      <div className="fw-label mb-0.5 truncate flex items-center gap-1">
        <span className="truncate">{label}{multi ? "" : " · single"}</span>
        {pending && <span data-testid={`multifilter-pending-${field}`} className="w-1.5 h-1.5 rounded-full bg-[color:var(--brand-mint)] shrink-0" title="Staged — press Apply" />}
      </div>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setQ(""); }}>
        <PopoverTrigger asChild>
          <button data-testid={`multifilter-select-${field}`} data-active={committed.length > 0}
            className="fw-selection w-full flex items-center gap-1 min-h-8 text-left">
            <span className="flex-1 min-w-0 flex items-center gap-1 overflow-hidden">
              {!committed.length && <span className="opacity-80">All</span>}
              {committed.slice(0, maxChips).map((v) => (
                <span key={v} data-testid={`multifilter-chip-${field}`} className="fw-chip inline-flex items-center gap-0.5 max-w-[110px] px-1.5 py-0.5 rounded-full text-[9px] truncate">
                  <span className="truncate">{v}</span>
                  <X className="w-2.5 h-2.5 shrink-0 cursor-pointer" onClick={(e) => { e.stopPropagation(); setSel(committed.filter((x) => x !== v)); }} />
                </span>
              ))}
              {committed.length > maxChips && <span className="text-[10px] text-[color:var(--brand-ocean)] shrink-0">+{committed.length - maxChips}</span>}
            </span>
            <ChevronDown className="fw-icon shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent data-testid={`multifilter-popup-${field}`} align="start" className="fw-dropdown w-[250px] p-2 space-y-1.5" style={filterStyleVars(opts)}>
          {(f.searchable !== false) && (
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
              <Input data-testid={`multifilter-search-${field}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-7 h-7 text-xs bg-[color:var(--surface-2)]" />
            </div>
          )}
          {multi && (
            <div className="flex items-center gap-2 px-0.5">
              <button data-testid={`multifilter-selectall-${field}`} onClick={() => setSel([...new Set([...selected, ...shown])])} className="text-[10px] text-[color:var(--brand-ocean)] hover:underline">Select all</button>
              <button data-testid={`multifilter-clearsel-${field}`} onClick={() => setSel([])} className="text-[10px] text-[color:var(--ink-3)] hover:underline">Clear</button>
              {selected.length > 0 && <span className="ml-auto text-[9px] text-[color:var(--ink-3)]">{selected.length} selected</span>}
            </div>
          )}
          <div className="max-h-[220px] overflow-auto space-y-0.5">
            {!multi && (
              <button onClick={() => { setSel([]); if (!applyMode) setOpen(false); }} data-on={!selected.length}
                className="fw-option w-full text-left px-2 py-1.5 rounded-md text-xs">All</button>
            )}
            {shown.map((v) => (
              <label key={v} data-testid={`multifilter-option-${field}-${v}`} data-on={selected.includes(v)}
                className="fw-option w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-pointer">
                {multi
                  ? <Checkbox checked={selected.includes(v)} onCheckedChange={() => toggle(v)} className="h-3.5 w-3.5" />
                  : <span className={cn("w-3 h-3 rounded-full border", selected.includes(v) ? "bg-[color:var(--brand-ocean)] border-[color:var(--brand-ocean)]" : "border-[color:var(--glass-border)]")} onClick={() => toggle(v)} />}
                <span className="truncate flex-1" onClick={() => toggle(v)}>{v}</span>
              </label>
            ))}
            {!shown.length && <p className="text-[10px] text-[color:var(--ink-3)] p-2 text-center">No values{q ? " match the search" : ""}.</p>}
          </div>
          {applyMode && (
            <div className="flex items-center gap-1.5 pt-1 border-t border-[color:var(--glass-border)]">
              <Button data-testid={`multifilter-done-${field}`} size="sm" className="h-6 text-[10px] flex-1 bg-[color:var(--brand-ocean)] text-white"
                onClick={() => { setOpen(false); onApplied && onApplied(); }}>Done</Button>
              <Button data-testid={`multifilter-clear-${field}`} size="sm" variant="outline" className="h-6 text-[10px] flex-1"
                onClick={() => setSel([])}>Clear</Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
